<template>
    <div>

        <kgr-note-box
            html
            title-key="basfi.generalNote.federation.title"
            text-key="basfi.generalNote.federation.text"
        />
        <br />
        <kgr-loading v-if="loading" />
        <div v-else>
            <kgr-form-scaffold
                :with-back-button="false"
                :pending="pending"
                @primaryBtnClicked="save"
            >
                <kgr-note-box
                    v-if="selectMode"
                    warning
                    html
                    title-key="basfi.letterOfAttorney.title"
                    text-key="basfi.letterOfAttorney.text"
                    style="margin-bottom: 15px"
                />
                <kgr-table class="kgr-table--block">
                    <thead>
                        <tr class="kgr-table__row">
                            <kgr-th v-if="selectMode" />
                            <kgr-th sortable>
                                {{ $t('basfi.kindergartens.tableheader.active') }}
                            </kgr-th>
                            <kgr-th sortable>
                                {{ $t('Einrichtung') }}
                            </kgr-th>
                            <kgr-th sortable>
                                {{ $t('basfi.kindergartens.tableheader.address') }}
                            </kgr-th>
                            <kgr-th sortable>
                                {{ $t('basfi.kindergartens.tableheader.basfiNumber') }}
                            </kgr-th>
                            <kgr-th sortable>
                                {{ $t('basfi.kindergartens.tableheader.costUnit') }}
                            </kgr-th>
                            <kgr-th sortable>
                                {{ $t('basfi.kindergartens.tableheader.subventionsEnabled') }}
                            </kgr-th>
                            <kgr-th sortable>
                                {{ $t('basfi.kindergartens.tableheader.start') }}
                            </kgr-th>
                            <kgr-th sortable>
                                {{ $t('basfi.kindergartens.tableheader.enabled') }}
                            </kgr-th>
                            <kgr-th
                                sortable
                                class="kgr-table__cell--actions"
                            >
                                {{ $t('options') }}
                            </kgr-th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="kindergartenConfig in kindergartenConfigs"
                            :key="kindergartenConfig.kindergarten.id"
                            class="kgr-table__row"
                        >
                            <td
                                v-if="selectMode"
                                class="kgr-table__cell"
                            >
                                <kgr-checkbox
                                    :checked="selected.find(kindergarten => kindergarten === kindergartenConfig.kindergarten) != null"
                                    @change="select($event, kindergartenConfig.kindergarten)"
                                />
                            </td>
                            <td class="kgr-table__cell">
                                <kgr-icon-active
                                    v-if="isRunning(kindergartenConfig)"
                                    small
                                    :title="$t('basfi.kindergartens.announcements.info.active')"
                                />
                                <kgr-icon-pause
                                    v-else-if="isPaused(kindergartenConfig)"
                                    small
                                    :title="$t('basfi.kindergartens.announcements.info.paused')"
                                />
                                <kgr-icon-active
                                    v-else
                                    small
                                    disabled
                                    :title="$t('basfi.kindergartens.announcements.info.inactive')"
                                />
                            </td>
                            <td class="kgr-table__cell">
                                <a
                                    :href="loginUrl(kindergartenConfig.kindergarten.id)"
                                    class="kgr-link kgr-link--unobtrusive"
                                >{{ kindergartenConfig.kindergarten.name }}</a>
                            </td>

                            <td class="kgr-table__cell">
                                {{ kindergartenConfig.kindergarten.street }}, {{ kindergartenConfig.kindergarten.zipcode }}
                                {{ kindergartenConfig.kindergarten.city }}
                            </td>

                            <td class="kgr-table__cell">
                                <div v-if="editKindergartenConfigs.indexOf(kindergartenConfig) !== -1">
                                    <kgr-text-input
                                        v-model="editKindergartenConfigs[editKindergartenConfigs.indexOf(kindergartenConfig)].basfiNumber"
                                        inline
                                        :disabled="editKindergartenConfigs[editKindergartenConfigs.indexOf(kindergartenConfig)].hasReports"
                                        :max-length="8"
                                        :error="getErrorMessageForField(kindergartenConfig,'basfiNumber')"
                                        @post-input="validate(kindergartenConfig)"
                                    />
                                </div>
                                <template v-else>
                                    {{ kindergartenConfig.basfiNumber }}
                                </template>
                            </td>
                            <td class="kgr-table__cell">
                                <div v-if="editKindergartenConfigs.indexOf(kindergartenConfig) !== -1">
                                    <kgr-cost-unit-select
                                        v-model="editKindergartenConfigs[editKindergartenConfigs.indexOf(kindergartenConfig)].costUnit"
                                        :options="kindergartenConfig.kindergarten.costUnits"
                                        display="name"
                                        :no-null-option="kindergartenConfig.hasPersistedCostUnit"
                                        inline
                                        :error="getErrorMessageForField(kindergartenConfig,'costUnit')"
                                        @post-input="validate(kindergartenConfig)"
                                    />
                                </div>
                                <template v-else>
                                    {{ kindergartenConfig.costUnit ? kindergartenConfig.costUnit.name : '' }}
                                </template>
                            </td>
                            <td
                                class="kgr-table__cell"
                                :data-sort="kindergartenConfig.kindergarten.subventionsEnabled ? 1 : 0"
                            >
                                <kgr-checkbox
                                    v-model="kindergartenConfig.kindergarten.subventionsEnabled"
                                    :readonly="editKindergartenConfigs.indexOf(kindergartenConfig) === -1"
                                    @change="validate(kindergartenConfig)"
                                />
                            </td>
                            <td class="kgr-table__cell">
                                <kgr-date-picker
                                    v-model="kindergartenConfig.start"
                                    :allowed-days="[2]"
                                    :error="getErrorMessageForField(kindergartenConfig,'start')"
                                    :display-only="editKindergartenConfigs.indexOf(kindergartenConfig) === -1 || editKindergartenConfigs[editKindergartenConfigs.indexOf(kindergartenConfig)].hasReports"
                                    @post-input="validate(kindergartenConfig)"
                                />
                            </td>
                            <td
                                class="kgr-table__cell"
                                :data-sort="kindergartenConfig.enabled ? 1 : 0"
                            >
                                <kgr-checkbox
                                    v-model="kindergartenConfig.enabled"
                                    :readonly="editKindergartenConfigs.indexOf(kindergartenConfig) === -1 || !canBeActivated(kindergartenConfig)"
                                />
                            </td>
                            <td class="kgr-table__cell kgr-table__cell--actions">
                                <kgr-icon-edit
                                    v-if="editKindergartenConfigs.indexOf(kindergartenConfig) === -1"
                                    :size="15"
                                    class="kgr-table__action kgr-clickable"
                                    :title="$t('basfi.action.changeSettings')"
                                    @click="edit(kindergartenConfig)"
                                />
                                <kgr-icon-reset
                                    v-else
                                    :size="15"
                                    :title="$t('basfi.action.reset')"
                                    class="kgr-table__action kgr-clickable"
                                    @click="reset(kindergartenConfig)"
                                />
                            </td>
                        </tr>
                    </tbody>
                </kgr-table>
                <template #additional-buttons>
                    <kgr-button
                        v-if="selectMode"
                        :value="$t('basfi.button.download.letterOfAttorney')"
                        type="grey"
                        :pending="pending"
                        @click="selectMode = false;downloadLetterOfAttorney()"
                    />
                    <kgr-button
                        v-if="!selectMode"
                        :value="$t('basfi.button.select.letterOfAttorney')"
                        type="grey"
                        :pending="pending"
                        @click="enabledSelectMode()"
                    />
                </template>
            </kgr-form-scaffold>
        </div>
    </div>
</template>

<script>
import BasfiKindergartenConfigRepository from '../../js/repository/BasfiKindergartenConfigRepository';
import CostUnitSelect from '../../components/common/form/select/GenericSelect.vue';
import DateObjectPicker from '../../components/common/form/picker/DateObjectPicker.vue';
import Error from '../../js/model/Error';
import FormScaffold from '../../components/common/form/FormScaffold';
import IconEdit from '../../components/common/icon/IconEdit';
import IconReset from '../../components/common/icon/IconReset';
import KgrButton from '../../components/common/form/button/KgrButton';
import KgrCheckbox from '../../components/common/form/checkbox/Checkbox.vue';
import KgrIconActive from '../../components/common/icon/IconActive';
import KgrIconPause from '../../components/common/icon/IconPause.vue';
import KgrNoteBox from '../../components/common/NoteBox.vue';
import Loading from '../../components/common/Loading';
import QueryParamEncoder from '../../js/service/QueryParamEncoder.js'
import TextInput from '../../components/common/form/input/TextInput';

export default {
    components: {
        'kgr-loading': Loading,
        'kgr-note-box': KgrNoteBox,
        'kgr-text-input': TextInput,
        'kgr-icon-edit': IconEdit,
        'kgr-icon-reset': IconReset,
        'kgr-button': KgrButton,
        'kgr-checkbox': KgrCheckbox,
        'kgr-icon-active': KgrIconActive,
        'kgr-icon-pause': KgrIconPause,
        'kgr-form-scaffold': FormScaffold,
        'kgr-cost-unit-select': CostUnitSelect,
        'kgr-date-picker': DateObjectPicker,
    },
    props: {
        federation: {
            type: Number,
            required: true
        },
        basfiEnabled: {
            type: Boolean,
            required: true
        },
    },
    data() {
        return {
            selectMode: false,
            selected: [],
            requireOnboarding: false,
            loading: true,
            pending: false,
            kindergartenConfigs: [],
            editKindergartenConfigs: [],
            error: new Error(),
        }
    },
    created() {
        this.load();
    },
    methods: {
        enabledSelectMode() {
            this.loading = true;
            this.selectMode = true;
            this.$nextTick(() => {
                this.loading = false; //workaround dataTable issue
            })
        },
        downloadLetterOfAttorney() {
            const queryParams = QueryParamEncoder.encodeArray(this.selected.map(kindergarten => kindergarten.id), 'ids');
            window.location = `/api/federations/${this.federation}/basfi/letter-of-attorney?${queryParams}`;
        },
        select(checked, kindergarten) {
            if(checked) {
                this.selected.push(kindergarten)
            } else {
                this.selected = this.selected.filter(k => k !== kindergarten)
            }
        },
        basfiNumberValid(number) {
            return number !== null && number.length === 8 && /[1-9]{1,8}/.test(number);
        },
        isRunning(config) {
            return config.kindergarten.subventionsEnabled &&
                this.basfiNumberValid(config.basfiNumber) &&
                config.start !== null &&
                config.enabled &&
                config.costUnit !== null;
        },
        isPaused(config) {
            return this.canBeActivated(config) && !config.enabled;
        },
        canBeActivated(config) {
            return config.kindergarten.subventionsEnabled &&
                this.basfiNumberValid(config.basfiNumber) &&
                config.start !== null &&
                config.costUnit !== null;
        },
        validate(config) {
            if(!this.canBeActivated(config) && config.enabled) {
                config.enabled = false;
            }
        },
        load() {
            BasfiKindergartenConfigRepository
                .findByFederationId(this.federation)
                .then(data => {
                    this.kindergartenConfigs = data;
                    this.loading = false;
                });
        },
        edit(kindergartenConfig) {
            if (this.editKindergartenConfigs.indexOf(kindergartenConfig) !== -1) {
                return;
            }

            this.editKindergartenConfigs.push(kindergartenConfig);
        },
        getErrorMessageForField(kindergartenConfig, property) {
            return this.error.messageForField(this.getErrorField(kindergartenConfig, property))
        },
        getErrorField(kindergartenConfig, property) {
            return 'configs[' + this.editKindergartenConfigs.indexOf(kindergartenConfig) + '].' + property;
        },
        clearErrors(kindergartenConfig) {
            this.error.clear(this.getErrorField(kindergartenConfig, 'basfiNumber'));
            this.error.clear(this.getErrorField(kindergartenConfig, 'costUnit'));
            this.error.clear(this.getErrorField(kindergartenConfig, 'start'));
        },

        reset(kindergartenConfig) {
            this.clearErrors(kindergartenConfig)
            this.editKindergartenConfigs = this.editKindergartenConfigs.filter(c => c !== kindergartenConfig);

            BasfiKindergartenConfigRepository
                .findByKindergartenId(kindergartenConfig.kindergarten.id)
                .then(data => {
                    const index = this.kindergartenConfigs.indexOf(kindergartenConfig);
                    this.$set(this.kindergartenConfigs, index, data);
                    this.loading = false;
                });
        },
        save() {
            if (this.editKindergartenConfigs.length > 0) {
                this.pending = true;
                BasfiKindergartenConfigRepository
                    .update(this.federation, this.editKindergartenConfigs)
                    .then(() => {
                        this.editKindergartenConfigs = [];
                        this.error = new Error();
                        this.$notify.success('saveChanges.success');
                    })
                    .catch(error => {
                        this.$notify.error(error.messageForField('configs') || 'saveChanges.error');
                        this.error = error;
                        this.$forceUpdate();
                    })
                    .finally(() => this.pending = false);
            }
        },
        loginUrl(id) {
            return `/federation/login/kindergarten/${id}`;
        },
    },
}
</script>
